"use client";

import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  type SelectChangeEvent,
  Autocomplete,
  CircularProgress,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import type React from "react";
import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { generateScreeningPDF } from "./donor-screening-table/generate-screening-pdf";
import { useSelector } from "react-redux";
import { result } from "lodash";
import SignatureCanvas from "react-signature-canvas";
import CancelIcon from "@mui/icons-material/Cancel";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import DevicesIcon from "@mui/icons-material/Devices";

const BIOMETRIC_API_BASE =
  process.env.REACT_APP_BIOMETRIC_API_URL || "https://127.0.0.1:50801/api";

type FingerprintDevice = { id: string; name: string };

const isFingerprintImage = (value: string) =>
  value.includes("image/jpeg") || value.includes("image/jpg");

interface FormData {
  exta: Record<string, any>;
  donorObjectId: string;
  status: string;
  fitnessStatus: string;
  bloodGroup: string;
  rhFactor: string;
  du: string;
  hbsAg: { result: string; value: string; cutoff: string };
  hcv: { result: string; value: string; cutoff: string };
  hiv: { result: string; value: string; cutoff: string };
  vdrl: { result: string; value: string; cutoff: string };
  mp: string;
  hgb: string;
  wbc: string;
  plt: string;
  remarks: string;
  donerType: string;
  technologistSignature: string;
  doctorSignature: string;
}

interface DonorDisplayData {
  name: string;
  gender: string;
  age: string;
  cellNo: string;
  cnicNo: string;
  weight: string;
  address: string;
  bloodSample: string;
  city: string;
  area: string;
}

interface DonorOption {
  _id: string;
  donorId: string;
  name: string;
  age: string;
  gender: string;
  cellNo: string;
  bloodGroup: string;
  rhFactor?: string;
  du?: string;
  cnicNo?: string;
  weight?: string;
  address?: string;
  bloodSample?: string;
  city?: string;
  area?: string;
}

// Helper function to display values
const displayValue = (value: string | number | undefined | null): string => {
  if (value == null || value === "") return "";
  return String(value);
};

// ScreeningTestRow component - moved outside to prevent re-creation on each render
const ScreeningTestRow: React.FC<{
  testName: string;
  testKey: "hbsAg" | "hcv" | "hiv" | "vdrl";
  formData: FormData;
  error: { [key: string]: string };
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleNestedSelectChange: (testKey: "hbsAg" | "hcv" | "hiv" | "vdrl", value: string) => void;
}> = ({ testName, testKey, formData, error, handleChange, handleNestedSelectChange }) => (
  <Grid container spacing={0.5} sx={{ mb: 1, alignItems: "center" }}>
    <Grid item xs={2}>
      <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
        {testName}:
      </Typography>
    </Grid>
    <Grid item xs={4}>
      <FormControl fullWidth size="small">
        <InputLabel>
          Result <span className="text-red-500">*</span>
        </InputLabel>
        <Select
          className="custom-input"
          label="Result"
          value={formData[testKey].result}
          onChange={(e: SelectChangeEvent<string>) =>
            handleNestedSelectChange(testKey, e.target.value)
          }
        >
          <MenuItem value="">Select</MenuItem>
          <MenuItem value="Positive">Positive</MenuItem>
          <MenuItem value="Negative">Negative</MenuItem>
          <MenuItem value="Not Tested">Not-Tested</MenuItem>
        </Select>
      </FormControl>
      <p className="text-red-500 text-[7px] ml-2">{error?.result}</p>
    </Grid>
    <Grid item xs={3}>
      <TextField
        className="custom-input"
        label="Value"
        name={`${testKey}.value`}
        value={displayValue(formData[testKey].value)}
        onChange={handleChange}
        fullWidth
        size="small"
      />
    </Grid>
    <Grid item xs={3}>
      <TextField
        className="custom-input"
        label="Cutoff"
        name={`${testKey}.cutoff`}
        value={displayValue(formData[testKey].cutoff)}
        onChange={handleChange}
        fullWidth
        size="small"
      />
    </Grid>
  </Grid>
);

const ScreeningSignatureBox = ({
  label,
  value,
  onChange,
  fingerprintDevice,
  detectingDevice,
  onDetectDevice,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  fingerprintDevice: FingerprintDevice | null;
  detectingDevice: boolean;
  onDetectDevice: () => void;
}) => {
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const [capturing, setCapturing] = useState(false);
  const fingerprint = Boolean(value) && isFingerprintImage(value);

  useEffect(() => {
    if (!value || fingerprint) return;
    const timer = window.setTimeout(() => {
      if (signatureRef.current && signatureRef.current.isEmpty()) {
        signatureRef.current.fromDataURL(value);
      }
    }, 200);
    return () => window.clearTimeout(timer);
  }, [value, fingerprint]);

  const clearSignature = () => {
    signatureRef.current?.clear();
    onChange("");
  };

  const handleCaptureFingerprint = async () => {
    if (!fingerprintDevice) {
      toast.error("Please detect the fingerprint device first");
      return;
    }
    setCapturing(true);
    try {
      const response = await axios.get(`${BIOMETRIC_API_BASE}/ScanBiomini`, {
        timeout: 60000,
      });
      const retcode = Number(response?.data?.retcode);
      const imageData = response?.data?.image?.data;
      const imageFormat = response?.data?.image?.format || "jpg";
      if (retcode === 1 && imageData) {
        signatureRef.current?.clear();
        onChange(`data:image/${imageFormat};base64,${imageData}`);
        toast.success("Fingerprint captured successfully");
      } else {
        toast.error("Fingerprint capture failed. Please try again.");
      }
    } catch (error) {
      console.error("Fingerprint capture failed:", error);
      toast.error(
        "Unable to capture fingerprint. Ensure the device is connected."
      );
    } finally {
      setCapturing(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: "11px", fontWeight: 700, mb: 0.5 }}>
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
        <Tooltip
          title={
            fingerprintDevice
              ? `Device Ready: ${fingerprintDevice.name}`
              : "Detect Device"
          }
        >
          <span>
            <IconButton
              size="small"
              color={fingerprintDevice ? "success" : "primary"}
              onClick={onDetectDevice}
              disabled={detectingDevice || capturing}
              sx={{
                border: "1px solid",
                borderColor: fingerprintDevice
                  ? "success.main"
                  : "primary.main",
                borderRadius: "4px",
                width: 26,
                height: 26,
              }}
            >
              {detectingDevice ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DevicesIcon sx={{ fontSize: 15 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Button
          size="small"
          variant="contained"
          startIcon={
            capturing ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <FingerprintIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={handleCaptureFingerprint}
          disabled={!fingerprintDevice || detectingDevice || capturing}
          sx={{
            fontSize: "11px",
            textTransform: "none",
            minHeight: 26,
            py: 0.25,
          }}
        >
          Capture Fingerprint
        </Button>
      </Box>
      <Box
        sx={{
          position: "relative",
          border: "1px solid #111",
          borderRadius: "4px",
          height: 180,
          background: "#fafafa",
          overflow: "hidden",
        }}
      >
        {fingerprint && value ? (
          <Box
            component="img"
            src={value}
            alt={label}
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <SignatureCanvas
            ref={signatureRef}
            penColor="black"
            backgroundColor="transparent"
            canvasProps={{
              width: 360,
              height: 180,
              style: {
                width: "100%",
                height: "180px",
                display: "block",
                touchAction: "none",
                cursor: "crosshair",
              },
            }}
            onEnd={() => {
              if (!signatureRef.current || signatureRef.current.isEmpty()) {
                onChange("");
                return;
              }
              onChange(signatureRef.current.toDataURL());
            }}
          />
        )}
        {value && (
          <Tooltip title="Clear">
            <CancelIcon
              onClick={clearSignature}
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                background: "red",
                color: "#fff",
                cursor: "pointer",
                fontSize: "16px",
                borderRadius: "50%",
                padding: "2px",
                zIndex: 1,
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const AddNewDonor: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    exta: {},
    donorObjectId: "",
    status: "Accepted",
    fitnessStatus: "fit",
    bloodGroup: "",
    rhFactor: "",
    du: "",
    hbsAg: { result: "Not Tested", value: "", cutoff: "1.0" },
    hcv: { result: "Not Tested", value: "", cutoff: "1.0" },
    hiv: { result: "Not Tested", value: "", cutoff: "1.0" },
    vdrl: { result: "Not Tested", value: "", cutoff: "1.0" },
    mp: "Negative",
    hgb: "",
    wbc: "",
    plt: "",
    remarks: "",
    donerType: "Voluntary",
    technologistSignature: "",
    doctorSignature: "",
  });

  const [donorDisplayData, setDonorDisplayData] =
    useState<DonorDisplayData | null>(null);
  const [donorInputValue, setDonorInputValue] = useState<string>("");
  const [donorOptions, setDonorOptions] = useState<DonorOption[]>([]);
  const [recentDonors, setRecentDonors] = useState<DonorOption[]>([]);
  const [selectedDonor, setSelectedDonor] = useState<DonorOption | null>(null);
  const [donorLoading, setDonorLoading] = useState<boolean>(false);
  const [resetKey, setResetKey] = useState<number>(0);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState<boolean>(false);
  const [submittedData, setSubmittedData] = useState<any>(null);
  const [error, setError] = useState<{ [key: string]: string }>({
    bloodGroup: "",
    rhFactor: "",
    result: "",
    fitnessStatus: "",
  });
  const [fingerprintDevice, setFingerprintDevice] =
    useState<FingerprintDevice | null>(null);
  const [detectingDevice, setDetectingDevice] = useState(false);
  const location = useLocation();
  const { state } = location;
  const { idd, id, data } = state || {};
  formData.donorObjectId = id || formData.donorObjectId;
  const profile = useSelector((state: any) => state?.profileSlice?.profile);

  const handleDetectFingerprintDevice = async (showErrorToast = true) => {
    setDetectingDevice(true);
    try {
      const response = await axios.get(`${BIOMETRIC_API_BASE}/devices`, {
        timeout: 10000,
      });
      const devices = response?.data?.Devices || [];
      const retcode = String(response?.data?.retcode ?? "");
      const fingerprintDeviceFound = devices.find(
        (device: { fingerprint?: string; id?: string; name?: string }) =>
          String(device?.fingerprint).toLowerCase() === "true"
      );
      if (retcode === "1" && fingerprintDeviceFound) {
        setFingerprintDevice({
          id: fingerprintDeviceFound.id,
          name: fingerprintDeviceFound.name || "BioMini",
        });
      } else {
        setFingerprintDevice(null);
        if (showErrorToast) {
          toast.error(
            "Fingerprint device not detected or configured. Please connect and configure the device."
          );
        }
      }
    } catch (error) {
      console.error("Fingerprint device detection failed:", error);
      setFingerprintDevice(null);
      if (showErrorToast) {
        toast.error(
          "Fingerprint device not detected or configured. Please connect and configure the device."
        );
      }
    } finally {
      setDetectingDevice(false);
    }
  };

  useEffect(() => {
    handleDetectFingerprintDevice(false);
    // Detect once when the screening form opens, same as donor consent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data) {
      // Extract blood group and Rh factor if available
      let bloodGroup = "";
      let rhFactor = "";

      if (data.bloodGroup) {
        // Parse blood group like "A+", "B-", etc.
        const match = data.bloodGroup.match(/^(A|B|AB|O)([+-])$/);
        if (match) {
          bloodGroup = match[1]; // e.g., "A"
          rhFactor = match[2]; // e.g., "+"
        } else {
          // If not in combined format, use as is
          bloodGroup = data.bloodGroup;
          rhFactor = data.rhFactor || "";
        }
      }

      setFormData(prev => ({
        ...prev,
        donorObjectId: id || data.donorObjectId || data.donorId?._id || prev.donorObjectId,
        // ...data,          // this line added by jamshaid and  commited by nadeem
        bloodGroup: bloodGroup || data.bloodGroup || "",
        rhFactor: rhFactor || data.rhFactor || "",
        status: data.status || prev.status,
        fitnessStatus: data.fitnessStatus || prev.fitnessStatus,
        du: data.du || prev.du,
        // Map nested objects, ensuring we have defaults if data is partial
        hbsAg: data.hbsAg
          ? {
              result: data.hbsAg.result || "Not Tested",
              value: String(data.hbsAg.value || ""),
              cutoff: String(data.hbsAg.cutoff || "1.0"),
            }
          : prev.hbsAg,
        hcv: data.hcv
          ? {
              result: data.hcv.result || "Not Tested",
              value: String(data.hcv.value || ""),
              cutoff: String(data.hcv.cutoff || "1.0"),
            }
          : prev.hcv,
        hiv: data.hiv
          ? {
              result: data.hiv.result || "Not Tested",
              value: String(data.hiv.value || ""),
              cutoff: String(data.hiv.cutoff || "1.0"),
            }
          : prev.hiv,
        vdrl: data.vdrl
          ? typeof data.vdrl === "object"
            ? {
                result: data.vdrl.result || "Not Tested",
                value: String(data.vdrl.value || ""),
                cutoff: String(data.vdrl.cutoff || "1.0"),
              }
            : {
                // Legacy string format (e.g. "Negative / Non-Reactive")
                result: String(data.vdrl).toLowerCase().includes("positive")
                  ? "Positive"
                  : String(data.vdrl).toLowerCase().includes("not tested")
                    ? "Not Tested"
                    : "Negative",
                value: "",
                cutoff: "1.0",
              }
          : prev.vdrl,
        mp: data.mp || prev.mp,
        hgb: String(data.hgb || ""),
        wbc: String(data.wbc || ""),
        plt: String(data.plt || ""),
        remarks: data.remarks || prev.remarks,
        donerType: data.donerType || prev.donerType,
        technologistSignature: data.technologistSignature || "",
        doctorSignature: data.doctorSignature || "",
      }));

      setDonorDisplayData({
        name: data.donorData?.name || data.name || "",
        gender: data.donorData?.gender || data.gender || "",
        age: data.donorData?.age || data.age || "",
        cellNo: data.donorData?.cellNo || data.cellNo || "",
        cnicNo: data.donorData?.cnicNo || data.cnicNo || "",
        weight: data.donorData?.weight || data.weight || "",
        address: data.donorData?.address || data.address || "",
        bloodSample: data.donorData?.bloodSample || data.bloodSample || "",
        city: data.donorData?.city || data.city || "",
        area: data.donorData?.area || data.area || "",
      });
    }
  }, [data, idd]);

  console.log("Donor ID:", idd, "Data:", data);
  const navigate = useNavigate();

  const fetchRecentDonors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/donor/getDonors`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setRecentDonors(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching recent donors", error);
    }
  };

  const fetchDonorOptions = async (search: string) => {
    if (!search) {
      setDonorOptions(recentDonors);
      return;
    }
    try {
      setDonorLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/donor/getAllDonorsByFilter`,
        {
          params: { search, searchBy: "donorId" },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setDonorOptions(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching donors", error);
      setDonorOptions([]);
    } finally {
      setDonorLoading(false);
    }
  };

  const debouncedFetch = useCallback(debounce(fetchDonorOptions, 300), [
    recentDonors,
  ]);

  useEffect(() => {
    fetchRecentDonors();
  }, []);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Handle nested fields like "hbsAg.value", "hcv.cutoff", etc.
    if (
      name.startsWith("hbsAg") ||
      name.startsWith("hcv") ||
      name.startsWith("hiv") ||
      name.startsWith("vdrl")
    ) {
      const [testKey, field] = name.split(".") as [
        "hbsAg" | "hcv" | "hiv" | "vdrl",
        "value" | "cutoff"
      ];
      setFormData(prev => ({
        ...prev,
        [testKey]: { ...prev[testKey], [field]: value },
      }));
    } else {
      // Handle regular fields like "hgb", "wbc", "plt", "remarks", etc.
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleNestedSelectChange = (
    testKey: "hbsAg" | "hcv" | "hiv" | "vdrl",
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [testKey]: { ...prev[testKey], result: value },
    }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Format date for PDF
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Handle print functionality
  const handlePrint = () => {
    if (!submittedData) {
      console.error("No screening data available");
      return;
    }

    // Get the performer name from profile
    const performerName =
      profile?.data?.fullName || profile?.data?.firstName || "";

    // Generate the PDF
    const doc = generateScreeningPDF(submittedData, formatDate, performerName);

    if (!doc) {
      toast.error("Failed to generate PDF");
      return;
    }

    // Create a blob from the PDF and print directly
    const pdfBlob = doc.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);

    // Create an invisible iframe for printing
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    // Set up print handler
    iframe.onload = () => {
      try {
        // Add a small delay to ensure the PDF is fully loaded
        setTimeout(() => {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();

            // Only clean up after print dialog is closed
            iframe.contentWindow.onafterprint = () => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(blobUrl);
              // Close the dialog and navigate after printing
              handlePrintComplete();
            };
          }
        }, 1000);
      } catch (e) {
        console.error("Error triggering print:", e);
        // Fallback if direct print fails
        window.open(blobUrl, "_blank");
        // Clean up the URL object
        URL.revokeObjectURL(blobUrl);
        // Still close the dialog and navigate
        handlePrintComplete();
      }
    };

    // Set iframe source to the PDF blob URL
    iframe.src = blobUrl;

    // Backup cleanup in case onafterprint doesn't fire
    // const backupCleanupTimer = setTimeout(() => {
    //   try {
    //     if (document.body.contains(iframe)) {
    //       document.body.removeChild(iframe)
    //       URL.revokeObjectURL(blobUrl)
    //       // Make sure we still navigate even if cleanup fails
    //       handlePrintComplete()
    //     }
    //   } catch (e) {
    //     console.error("Error during backup cleanup:", e)
    //     handlePrintComplete()
    //   }
    // }, 60000) // 1 minute

    handlePrintComplete();
  };

  const handlePrintComplete = () => {
    setIsPrintDialogOpen(false);
    // Navigate to the next page
    if (submittedData) {
      navigate("/donor-bag", {
        state: {
          data: submittedData,
          idd: submittedData.donorObjectId,
        },
      });
    }
    handleClear();
  };
  const validateForm = () => {
    const newErrors = {
      rhFactor: "",
      bloodGroup: "",
      du: "",
      result: "",
      fitnessStatus: "",
    };

    let isValid = true;

    // Validate required fields
    if (!formData.bloodGroup) {
      newErrors.bloodGroup = "Blood Group is required";
      isValid = false;
    }
    if (!formData.rhFactor) {
      newErrors.rhFactor = "Rh Factor is required";
      isValid = false;
    }
    if (!formData.fitnessStatus) {
      newErrors.fitnessStatus = "Fitness Status is required";
      isValid = false;
    }

    // Update error state
    setError(newErrors);
    return isValid;
  };

  // Helper function to prepare data for API submission
  // Converts string values to numbers as required by the backend
  const prepareDataForSubmission = (data: FormData) => {
    return {
      ...data,
      hbsAg: {
        ...data.hbsAg,
        value: data.hbsAg.value === "" ? 0 : Number(data.hbsAg.value),
        cutoff: data.hbsAg.cutoff === "" ? 1.0 : Number(data.hbsAg.cutoff),
      },
      hcv: {
        ...data.hcv,
        value: data.hcv.value === "" ? 0 : Number(data.hcv.value),
        cutoff: data.hcv.cutoff === "" ? 1.0 : Number(data.hcv.cutoff),
      },
      hiv: {
        ...data.hiv,
        value: data.hiv.value === "" ? 0 : Number(data.hiv.value),
        cutoff: data.hiv.cutoff === "" ? 1.0 : Number(data.hiv.cutoff),
      },
      vdrl: {
        ...data.vdrl,
        value: data.vdrl.value === "" ? 0 : Number(data.vdrl.value),
        cutoff: data.vdrl.cutoff === "" ? 1.0 : Number(data.vdrl.cutoff),
      },
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.donorObjectId) {
      toast.warning("Please select the patient");
      return;
    }
    if (!validateForm()) {
      return;
    }
    if (idd) {
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_BLOOD_BANK}/donor-screening/updateScreeningById/${idd}`,
          prepareDataForSubmission(formData),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.data.isSuccess === true) {
          toast.success("Donor screening updated successfully.");
          navigate("/donor-screening-reports");
          handleClear();
        } else {
          toast.warning(
            response.data.message || "Failed to update donor screening."
          );
        }
      } catch (error: any) {
        console.error("Error updating donor screening", error);
        toast.error("Failed to update donor screening.");
      }
    } else {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BLOOD_BANK}/donor-screening/createScreening`,
          prepareDataForSubmission(formData),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.data.isSuccess === true) {
          toast.success("Donor screening added successfully.");
          // Store the submitted data for printing
          setSubmittedData(response.data.data);
          // Open the print dialog
          setIsPrintDialogOpen(true);
        } else {
          toast.warning(
            response.data.message || "Failed to add donor screening."
          );
        }
      } catch (error: any) {
        console.error("Error adding donor screening", error);
        toast.error("Failed to add donor screening.");
      }
    }
  };

  const handleClear = () => {
    setFormData({
      exta: {},
      donorObjectId: "",
      status: "Accepted",
      bloodGroup: "",
      rhFactor: "",
      du: "",
      hbsAg: { result: "Not Tested", value: "", cutoff: "1.0" },
      hcv: { result: "Not Tested", value: "", cutoff: "1.0" },
      hiv: { result: "Not Tested", value: "", cutoff: "1.0" },
      vdrl: { result: "Not Tested", value: "", cutoff: "1.0" },
      mp: "Negative",
      hgb: "",
      wbc: "",
      plt: "",
      remarks: "",
      fitnessStatus: "fit",
      donerType: "Voluntary",
      technologistSignature: "",
      doctorSignature: "",
    });
    setDonorInputValue("");
    setSelectedDonor(null);
    setDonorDisplayData(null);
    setResetKey(prev => prev + 1);
  };

  // const isFormValid = () =>
  //   [formData.bloodGroup, formData.rhFactor, formData.donerType].every((field) =>
  //     typeof field === "string" ? field.trim() !== "" : field !== 0,
  //   )


  return (
    <Box sx={{ p: 1, maxHeight: "100vh", overflow: "auto" }}>
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "8px",
          pl: 1,
          pr: 1,
          p: 0.5,
          mb: 1,
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Grid item>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "bold", marginLeft: 1 }}
            >
              {idd ? "Update Donor Screening" : "Donor Screening"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8} md={4} lg={4}>
            <Autocomplete
              className="custom-input"
              key={resetKey}
              options={donorOptions}
              getOptionLabel={option =>
                `${option.donorId} ${option.name || "Unknown"}`
              }
              inputValue={donorInputValue}
              value={null}
              onInputChange={(e, newInputValue) => {
                setDonorInputValue(newInputValue);
                debouncedFetch(newInputValue);
              }}
              onChange={(e, value) => {
                if (value) {
                  // Extract blood group and Rh factor if available
                  let bloodGroup = "";
                  let rhFactor = "";

                  if (value.bloodGroup) {
                    // Parse blood group like "A+", "B-", etc.
                    const match = value.bloodGroup.match(/^(A|B|AB|O)([+-])$/);
                    if (match) {
                      bloodGroup = match[1]; // e.g., "A"
                      rhFactor = match[2]; // e.g., "+"
                    }
                  }

                  setFormData(prev => ({
                    ...prev,
                    donorObjectId: value._id,
                    bloodGroup: bloodGroup || value.bloodGroup || "",
                    rhFactor: rhFactor || "",
                    du: value.du || prev.du || "",
                  }));

                  setDonorDisplayData({
                    name: value.name,
                    gender: value.gender || "",
                    age: value.age || "",
                    cellNo: value.cellNo || "",
                    cnicNo: value.cnicNo || "",
                    weight: value.weight || "",
                    address: value.address || "",
                    bloodSample: value.bloodSample || "Received",
                    city: value.city || "",
                    area: value.area || "",
                  });
                  setSelectedDonor(value);
                  setDonorInputValue("");
                } else {
                  setFormData(prev => ({
                    ...prev,
                    donorObjectId: "",
                    bloodGroup: "",
                    rhFactor: "",
                    du: "",
                  }));
                  setDonorDisplayData(null);
                  setSelectedDonor(null);
                  setDonorInputValue("");
                }
              }}
              onOpen={() => !donorInputValue && setDonorOptions(recentDonors)}
              loading={donorLoading}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Select or Search by Donor ID"
                  size="small"
                  fullWidth
                  className="custom-input"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {donorLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>
      <Card sx={{ mb: 1, bgcolor: "#fff", borderRadius: "8px" }}>
        <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
          <Typography
            variant="h6"
            sx={{ fontSize: "14px", fontWeight: "bold", mb: 0.5 }}
          >
            Donor Information
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={4} sm={2}>
              <Typography sx={{ fontSize: "12px" }}>
                <strong>Name:</strong>{" "}
                {donorDisplayData ? donorDisplayData.name : "...."}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Typography sx={{ fontSize: "12px" }}>
                <strong>Gender:</strong>{" "}
                {donorDisplayData ? donorDisplayData.gender : "...."}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Typography sx={{ fontSize: "12px" }}>
                <strong>Age:</strong>{" "}
                {donorDisplayData ? donorDisplayData.age : "...."}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Typography sx={{ fontSize: "12px" }}>
                <strong>Cell No:</strong>{" "}
                {donorDisplayData ? donorDisplayData.cellNo : " ...."}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Typography sx={{ fontSize: "12px" }}>
                <strong>City:</strong>{" "}
                {donorDisplayData ? donorDisplayData.city : "...."}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Typography sx={{ fontSize: "12px" }}>
                <strong>Area:</strong>{" "}
                {donorDisplayData ? donorDisplayData.area : "...."}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Box sx={{ bgcolor: "#fff", p: 1, borderRadius: "8px" }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    <Grid item md={4} xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel
                          sx={{
                            top: "-5px", // adjust based on height
                          }}
                        >
                          Blood Group <span className="text-red-500">*</span>
                        </InputLabel>
                        <Select
                          label="Blood Group"
                          className="custom-input"
                          name="bloodGroup"
                          value={formData.bloodGroup}
                          onChange={handleSelectChange}
                          // sx={{
                          //   borderRadius: "12px",
                          //   background: "#F5F5FB",
                          //   fontSize: "12px",
                          //   boxShadow: "none",
                          //   height: "38px",
                          //   "& fieldset": { border: "none" },
                          // }}
                        >
                          <MenuItem value="">Select</MenuItem>
                          <MenuItem value="A">A</MenuItem>
                          <MenuItem value="B">B</MenuItem>
                          <MenuItem value="AB">AB</MenuItem>
                          <MenuItem value="O">O</MenuItem>
                        </Select>
                      </FormControl>
                      <p className="text-red-500 text-[10px] ml-2">
                        {error?.bloodGroup}
                      </p>
                    </Grid>
                    <Grid item md={4} xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel
                          sx={{
                            top: "-5px", // adjust based on height
                          }}
                        >
                          Rh Factor <span className="text-red-500">*</span>
                        </InputLabel>
                        <Select
                          className="custom-input"
                          label="Rh Factor"
                          name="rhFactor"
                          value={formData.rhFactor}
                          onChange={handleSelectChange}
                          // sx={{
                          //   borderRadius: "12px",
                          //   background: "#F5F5FB",
                          //   fontSize: "12px",
                          //   boxShadow: "none",
                          //   height: "38px",
                          //   "& fieldset": { border: "none" },
                          // }}
                        >
                          <MenuItem value="">Select</MenuItem>
                          <MenuItem value="+">+</MenuItem>
                          <MenuItem value="-">-</MenuItem>
                        </Select>
                      </FormControl>
                      <p className="text-red-500 text-[10px] ml-2">
                        {error?.rhFactor}
                      </p>
                    </Grid>
                    <Grid item md={4} xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel
                          sx={{
                            top: "-5px", // adjust based on height
                          }}
                        >
                          DU
                        </InputLabel>
                        <Select
                          className="custom-input"
                          label="DU"
                          name="du"
                          size="small"
                          value={formData.du}
                          onChange={handleSelectChange}
                          // sx={{
                          //   borderRadius: "12px",
                          //   background: "#F5F5FB",
                          //   fontSize: "12px",
                          //   boxShadow: "none",
                          //   height: "38px",
                          //   "& fieldset": { border: "none" },
                          // }}
                        >
                          <MenuItem value="">Select</MenuItem>
                          <MenuItem value="Positive">DU+</MenuItem>
                          <MenuItem value="Negative">Du-</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    <Grid item md={6} xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Donor Type</InputLabel>
                        <Select
                          className="custom-input"
                          label="Donor Type"
                          name="donerType"
                          value={formData.donerType}
                          onChange={handleSelectChange}
                          // sx={{
                          //   borderRadius: "12px",
                          //   background: "#F5F5FB",
                          //   fontSize: "12px",
                          //   boxShadow: "none",
                          //   height: "38px",
                          //   "& fieldset": { border: "none" },
                          // }}
                        >
                          <MenuItem value="">Select</MenuItem>
                          <MenuItem value="Voluntary">Voluntary</MenuItem>
                          <MenuItem value="Replacement">Replacement</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <FormControl component="fieldset" size="small">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Status <span className="text-red-500">*</span>
                          </Typography>
                          <RadioGroup
                            row
                            name="status"
                            value={formData.status}
                            onChange={handleRadioChange}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "11px",
                              },
                            }}
                          >
                            <FormControlLabel
                              value="Accepted"
                              control={<Radio size="small" />}
                              label="Accepted"
                            />
                            <FormControlLabel
                              value="Deferred"
                              control={<Radio size="small" />}
                              label="Deferred"
                            />
                            <FormControlLabel
                              value="Not Bled"
                              control={<Radio size="small" />}
                              label="Not Bled"
                            />
                          </RadioGroup>
                        </Stack>
                      </FormControl>
                      <p className="text-red-500 text-[10px] ml-2">
                        {error?.fitnessStatus}
                      </p>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    sx={{ fontSize: "11px", fontWeight: "bold", mb: 0.5 }}
                  >
                    Lab (Complete Blood Count)
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item md={4} xs={6}>
                      {/* HGB as TextField */}
                      <TextField
                        className="custom-input"
                        label="HGB"
                        name="hgb"
                        value={formData.hgb}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        // className="search-field"
                        // sx={{
                        //   borderRadius: "12px",
                        //   background: "#F5F5FB",
                        //   fontSize: "12px",
                        //   boxShadow: "none",
                        //   height: "38px",
                        //   "& .MuiInputBase-root": { height: "38px" },
                        // }}
                      />
                    </Grid>
                    <Grid item md={4} xs={6}>
                      {/* WBC as TextField */}
                      <TextField
                        className="custom-input"
                        label="WBC"
                        name="wbc"
                        value={formData.wbc}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        // className="search-field"
                        // sx={{
                        //   borderRadius: "12px",
                        //   background: "#F5F5FB",
                        //   fontSize: "12px",
                        //   boxShadow: "none",
                        //   height: "38px",
                        //   "& .MuiInputBase-root": { height: "38px" },
                        // }}
                      />
                    </Grid>
                    <Grid item md={4} xs={6}>
                      {/* PLT as TextField */}
                      <TextField
                        className="custom-input"
                        label="PLT"
                        name="plt"
                        value={formData.plt}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        // className="search-field"
                        // sx={{
                        //   borderRadius: "12px",
                        //   background: "#F5F5FB",
                        //   fontSize: "12px",
                        //   boxShadow: "none",
                        //   height: "38px",
                        //   "& .MuiInputBase-root": { height: "38px" },
                        // }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    sx={{ fontSize: "11px", fontWeight: "bold", mb: 0.5 }}
                  >
                    Remarks/Comments
                  </Typography>
                  <TextField
                    className="custom-input"
                    label="Remarks"
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    fullWidth
                    // className="search-field"
                    size="small"
                    multiline
                    rows={1}
                    // sx={{ "& .MuiInputBase-root": { minHeight: "32px" } }}
                  />
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <ScreeningSignatureBox
                        key={`technologist-${resetKey}`}
                        label="Technologist Signature/Thumb Impression"
                        value={formData.technologistSignature}
                        onChange={value =>
                          setFormData(prev => ({
                            ...prev,
                            technologistSignature: value,
                          }))
                        }
                        fingerprintDevice={fingerprintDevice}
                        detectingDevice={detectingDevice}
                        onDetectDevice={() =>
                          handleDetectFingerprintDevice(true)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ScreeningSignatureBox
                        key={`doctor-${resetKey}`}
                        label="Doctor's Signature / Thumb Impression"
                        value={formData.doctorSignature}
                        onChange={value =>
                          setFormData(prev => ({
                            ...prev,
                            doctorSignature: value,
                          }))
                        }
                        fingerprintDevice={fingerprintDevice}
                        detectingDevice={detectingDevice}
                        onDetectDevice={() =>
                          handleDetectFingerprintDevice(true)
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  bgcolor: "#fff",
                  p: 1.5,
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                  height: "100%",
                }}
              >
                <Typography
                  sx={{ fontSize: "12px", fontWeight: "bold", mb: 1 }}
                >
                  Latex Screening Results
                </Typography>
                <Grid container spacing={0.5} sx={{ mb: 0.5, alignItems: "end" }}>
                  <Grid item xs={2} />
                  <Grid item xs={4}>
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 700,
                        textAlign: "center",
                        lineHeight: 1.2,
                      }}
                    >
                      ICT Screening
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 700,
                        textAlign: "center",
                        lineHeight: 1.2,
                      }}
                    >
                      CLIA/ELISA Method
                    </Typography>
                  </Grid>
                </Grid>
                <ScreeningTestRow 
                  testName="HBs Ag" 
                  testKey="hbsAg" 
                  formData={formData}
                  error={error}
                  handleChange={handleChange}
                  handleNestedSelectChange={handleNestedSelectChange}
                />
                <ScreeningTestRow 
                  testName="HCV" 
                  testKey="hcv" 
                  formData={formData}
                  error={error}
                  handleChange={handleChange}
                  handleNestedSelectChange={handleNestedSelectChange}
                />
                <ScreeningTestRow 
                  testName="HIV" 
                  testKey="hiv" 
                  formData={formData}
                  error={error}
                  handleChange={handleChange}
                  handleNestedSelectChange={handleNestedSelectChange}
                />
                <ScreeningTestRow 
                  testName="VDRL" 
                  testKey="vdrl" 
                  formData={formData}
                  error={error}
                  handleChange={handleChange}
                  handleNestedSelectChange={handleNestedSelectChange}
                />
                <Grid container spacing={0.5} sx={{ mb: 1, alignItems: "center" }}>
                  <Grid item xs={2}>
                    <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                      MP:
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Result</InputLabel>
                      <Select
                        className="custom-input"
                        label="Result"
                        name="mp"
                        value={formData.mp}
                        onChange={e => {
                          setFormData(prev => ({
                            ...prev,
                            mp: e.target.value,
                          }));
                        }}
                      >
                        <MenuItem value="Negative">Negative</MenuItem>
                        <MenuItem value="Positive">Positive</MenuItem>
                        <MenuItem value="Not Tested">Not Tested</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <FormControl component="fieldset" size="small">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Fitness Status
                    </Typography>
                    <RadioGroup
                      row
                      name="fitnessStatus"
                      value={formData.fitnessStatus}
                      onChange={handleRadioChange}
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "11px",
                        },
                      }}
                    >
                      <FormControlLabel
                        value="fit"
                        control={<Radio size="small" />}
                        label="Fit"
                      />
                      <FormControlLabel
                        value="unfit"
                        control={<Radio size="small" />}
                        label="unfit"
                      />
                      <FormControlLabel
                        value="Deferred"
                        control={<Radio size="small" />}
                        label="Deferred"
                      />
                    </RadioGroup>
                  </Stack>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
          <Box
            sx={{
              mt: 1.5,
              pt: 1,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Button
              onClick={handleClear}
              variant="outlined"
              size="small"
              sx={{ height: "30px", minWidth: "80px" }}
            >
              Clear
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="small"
              // disabled={!isFormValid()}
              sx={{ height: "30px", minWidth: "80px" }}
            >
              {idd ? "Update" : "Submit"}
            </Button>
          </Box>
        </form>
      </Box>

      {/* Print Dialog */}
      <Dialog open={isPrintDialogOpen} onClose={() => handlePrintComplete()}>
        <DialogTitle>Print Screening Report</DialogTitle>
        <DialogContent>
          <Typography>
            Would you like to print the donor screening report?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handlePrintComplete()} color="primary">
            Skip
          </Button>
          <Button onClick={handlePrint} color="primary" variant="contained">
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddNewDonor;
